import { Translations } from "shared/types/languages";

export enum ModuleGameStatus {
  Active = "Active",
  Hidden = "Hidden",
}

export enum TagMatchingType {
  EQUALS = "AND",
  NOT_EQUALS = "NOT",
}

export type ModuleGame = {
  id: string;
  status: ModuleGameStatus;
};

export type TagMatchRule = {
  primaryTagId: string;
  matchType: TagMatchingType;
};

export type SelectedGame = {
  id: string;
  name: string | null;
  order: number;
  status: ModuleGameStatus;
  studioId: number | null;
  studioName?: string;
  tagTypes?: string[];
  tags?: string[];
};

export type ModuleData = {
  id: string;
  name: string;
  type: ModuleType;
  layout: ModuleLayout | null;
  status: ModuleStatus;
  adminNotes: string | null;
  createdAt: Date;
  updatedAt: Date;
  tagTypes: string[];
  tags: string[];
  rows?: number;
  gamesOrderType: GamesOrderType | null;
  manualSelectedGames: ModuleGame[];
  tagSelectedGames: ModuleGame[];
  selectedGames: ModuleGame[];
  selectedStudiosIds: string[];
  orderedGamesStudiosIds: string[];
  selectedGamesIdsByStudio: Record<string, string[]>;
  studioFilterEnabled: boolean;
  baseUrl?: string;
  contentBlocks?: ModuleContentBlocksContent[];
  topPrivacyTipsBlock?: ModuleTopPrivacyTipsBlockContent;
  bonusObtainType?: BonusObtainType | null;
  translations?: Translations;
  icon: ModuleIcon | null;
  tagsMatchRules?: TagMatchRule[];
};

export type ModuleDataToCreate = Omit<
  ModuleData,
  "id" | "createdAt" | "updatedAt"
>;

export type ModuleDataToUpdate = Partial<ModuleDataToCreate>;

export enum ModuleType {
  Games = "games",
  Studios = "studios",
  MarqueeCards = "marqueeCards",
  ProductIframe = "productIframe",
  ContentBlocks = "contentBlocks",
  Promotion = "promotion",
  RecentlyPlayed = "recentlyPlayed",
  FavouriteGames = "favouriteGames",
  RecentWithdrawals = "recentWithdrawals",
  TopWinners = "topWinners",
  TopPrivacyTips = "topPrivacyTips",
}

export enum GamesOrderType {
  Random = "random",
  Manual = "manual",
}

export enum BonusObtainType {
  Obtainable = "obtainable",
  Active = "active",
}

export type ModuleLayout =
  | GamesModuleLayout
  | StudiosModuleLayout
  | ContentBlocksModuleLayout
  | PromotionModuleLayout;

export enum GamesModuleLayout {
  VerticalTiles = "verticalTiles",
  VerticalTilesStacked = "verticalTilesStacked",
  HorizontalTiles = "horizontalTiles",
  HorizontalTilesStacked = "horizontalTilesStacked",
  Top10Games = "top10Games",
  BigHorizontalTiles = "bigHorizontalTiles",
}

export enum StudiosModuleLayout {
  HorizontalLogos = "horizontalLogos",
}

export enum ContentBlocksModuleLayout {
  ThreeCardBlock = "threeCardBlock",
  TextBlock = "textBlock",
  OneCardBlock = "oneCardBlock",
}

export enum PromotionModuleLayout {
  GridView = "gridView",
  HorizontalScroll = "horizontalScroll",
  FeaturedCarousel = "featuredCarousel",
}

export enum ModuleStatus {
  Active = "active",
  Draft = "draft",
}

export type UpdateStatusParams = {
  moduleId: string;
  status: ModuleStatus;
};

export type ModuleTagTypeBlock = {
  id: string;
  selectedTagTypeId: string | null;
  selectedTags: Option[];
  tagCategory: string | null;
};

export type Option = {
  id: string;
  name: string;
};

export type ModuleWithPagePath = ModuleData & {
  redirectionPath: string;
};

export type CardBlockImage = File | null;

export type ModuleContentBlocksContent = {
  title: string;
  description: string;
  backgroundImage?: string | null;
  ctaForRegisteredUser?: string;
  ctaForUnRegisteredUser?: string;
  ctaDestination?: string;
  selectedGame?: { value: string; label: string };
  url?: string;
  urlOpensInNewTab?: boolean;
};

export type ModuleTopPrivacyTipsBlockContent = {
  title: string;
  description: string;
  backgroundImage: string | null;
  ctaForRegisteredUser: string;
  ctaForUnRegisteredUser: string;
};

// https://www.figma.com/design/kzSk2GSd5vst7B4qtMVZoR/BetEast--%3E-CMS-UX%2FUI?node-id=1193-4833&m=dev
export enum ModuleIcon {
  CheckmarkBadge = "checkmarkBadge",
  Chip = "chip",
  QuestionMark = "questionMark",
  Cards = "cards",
  Dice = "dice",
  Play = "play",
  Roulette = "roulette",
  Heart = "heart",
  Star = "star",
  Fire = "fire",
  Recently = "recently",
  Gift = "gift",
  Ticket = "ticket",
  TicketDiscount = "ticketDiscount",
  Puzzle = "puzzle",
  Time = "time",
  Info = "info",
  BadgeDiscount = "badgeDiscount",
  Trophy = "trophy",
}
